import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ShadowBlock from '../../../../atoms/ShadowBlock';
import Form from '../../../../atoms/Form';
import MButton from '../../../../MUI/Button/MButton';
import Field from '../../../../atoms/Field';
import { getCaseSummaryReportAsPdf } from '../../../../../store/slices/reportsSlice';
import Dropdown from '../../../../atoms/Dropdown';
import Table from '../../../../atoms/Table/Table';
import notificationUtils from '../../../../../utils/notificationUtils';
import EmptyBlock from '../../../../molecules/EmptyBlock';
import DateOrTimePicker from '../../../../atoms/DateOrTimePicker';
import { ReactComponent as Calender } from '../../../../../assets/icons/Calender.svg';
import { ReactComponent as ExportIcon } from '../../../../../assets/icons/document-download-blue.svg';
import ProgressModal from '../../ProductivityByAttorney/ProgressModal';
import { openOrDownloadBinary } from '../../../../../utils/binaryHelpers';
import { getCommunitiesList } from '../../../../../store/slices/clientPortalAdminUsers';
import CheckboxSelect from '../../../../atoms/CheckboxSelect';
import { useAvailableStatesList } from '../../../../../hooks/useAvailableStatesList';
import { caseSummaryForm, productivityByAttorneyColumns } from './CaseSummaryReport.constants';
import { apiClient } from '../../../../../lib/apiClient';

const CaseSummary = () => {
  const userStates = useAvailableStatesList();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [dateRange, setDateRange] = useState('');
  const [currentStateName, setCurrentStateName] = useState('');
  const [lastReportId, setLastReportId] = useState(null);
  const [timer, setTimer] = useState(false);
  const [ranges, setRanges] = useState(null);

  const { communities } = useSelector((state) => state.clientPortalAdminUsers);
  const dispatch = useDispatch();

  const form = useForm({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const getCurrentRange = (fromDate, toDate) =>
    `${moment(fromDate).format('L')} - ${moment(toDate).format('L')}`;

  const getOptions = (name) => {
    switch (name) {
      case 'stateCode':
        return userStates;
      case 'community':
        return (
          communities?.items?.map((item) => ({
            value: item?.communityId,
            label: item?.communityName,
          })) || []
        );

      default:
        return [];
    }
  };

  const getProductivityReport = async (id) => {
    const {
      data: { reportStatus, items },
    } = await apiClient.get(`/api/reports/caseSummary/${id}`);
    if (reportStatus?.code === 'Completed') {
      setIsLoading(false);
      const totalSoughtAmount = items?.reduce(
        (acc, item) => acc + (item?.proposedAmountSought || 0),
        0,
      );

      setTableData([...items, { isTotalRow: true, totalSoughtAmount }]);
    } else {
      const timerKey = setTimeout(() => {
        getProductivityReport(id);
      }, 3000);
      setTimer(timerKey);
    }
  };

  const handleSubmit = async (values) => {
    const { fromDate, toDate, stateCode, community } = values;
    setIsLoading(true);
    setCurrentStateName(stateCode?.value === 'AZ' ? 'Arizona' : 'Nevada');

    const reqBody = {
      fromDate: moment(fromDate).format('YYYY-MM-DD'),
      toDate: moment(toDate).format('YYYY-MM-DD'),
      stateCode: stateCode?.value,
      communityIds: community?.map(({ value }) => value),
    };

    const { data } = await apiClient.post(`/api/reports/caseSummary`, reqBody);
    if (data.reportId) {
      setDateRange(getCurrentRange(fromDate, toDate));
      setLastReportId(data.reportId);
      getProductivityReport(data.reportId);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
  }, [timer]);

  useEffect(() => {
    dispatch(getCommunitiesList({ pageSize: 100 }));
  }, []);

  const rowsAdapter = useMemo(() => {
    return tableData?.map((item) => {
      return {
        ...item,
        requestedOn: item?.requestedOn ? moment(item?.requestedOn).format('MM/DD/YYYY') : null,
        caseReferenceNumber: (
          <Link to={`/cp/cases/case/${item.caseId}`} target="_blank">
            <Typography
              variant="link"
              color="text.primary"
              sx={{
                cursor: 'pointer',
                textDecorationLine: 'underline',
                textTransform: 'none',
              }}
            >
              {item.caseReferenceNumber}
            </Typography>
          </Link>
        ),
      };
    });
  }, [tableData]);

  const getRanges = (name) => {
    const currentFromDate = form.getValues('fromDate');
    const currentToDate = form.getValues('toDate');
    if (name.includes('fromDate')) {
      setRanges({ toDate: { min: currentFromDate } });
    }

    if (name.includes('toDate')) {
      setRanges({ fromDate: { max: currentToDate } });
    }
  };

  const exportToPdf = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      const data = await dispatch(getCaseSummaryReportAsPdf(lastReportId)).unwrap();
      openOrDownloadBinary({
        content: data.content,
        fileName: data.originalFileName,
        contentType: data.contentType,
      });
    } catch (error) {
      notificationUtils.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack direction="column" mb={6}>
      <ShadowBlock>
        <ProgressModal isOpen={isLoading} />
        <Form form={form} onSubmit={handleSubmit}>
          {caseSummaryForm.map((i) => (
            <Stack
              direction="row"
              alignItems="center"
              columnGap={3}
              rowGap={2}
              flexWrap="wrap"
              py={1}
            >
              {i.inputBoxes.map((item) => {
                if (item.isDropdown) {
                  return (
                    <Field
                      key={item.name}
                      name={item.name}
                      isMandatory={item.isMandatory}
                      render={({ field, onCustomChange }) => (
                        <Dropdown
                          value={field.value}
                          isAddDisabled={false}
                          isSearchable
                          label={item.label}
                          width={item.width}
                          isColumn
                          placeholder={item.placeholder}
                          options={getOptions(item.name)}
                          onChange={onCustomChange(field.onChange)}
                          isMandatory={item.isMandatory}
                        />
                      )}
                    />
                  );
                }
                if (item.isDatePicker) {
                  return (
                    <Field
                      name={item.name}
                      key={item.name}
                      isMandatory={item.isMandatory}
                      render={({ field, onCustomChange, error }) => (
                        <DateOrTimePicker
                          name={item.name}
                          label={item.label}
                          isDate
                          error={error}
                          Svg={Calender}
                          selected={field.value}
                          isMandatory={item.isMandatory}
                          width={item.width}
                          height={item.height}
                          placeholder={item.placeholder}
                          minDate={ranges?.[item.name]?.min || null}
                          maxDate={ranges?.[item.name]?.max || moment()}
                          onClose={() => getRanges(item.name)}
                          setSelectedTimeOrDate={onCustomChange(field.onChange)}
                        />
                      )}
                    />
                  );
                }
                if (item.isCheckboxSelect) {
                  return (
                    <Field
                      key={item.name}
                      isMandatory={item.isMandatory}
                      name={item.name}
                      render={({ field, onCustomChange, error }) => (
                        <CheckboxSelect
                          label={item.label}
                          value={field.value}
                          width={item.width}
                          error={error}
                          isMandatory={item.isMandatory}
                          isDisabled={item.isDisabled}
                          placeholder={item.placeholder}
                          onChange={onCustomChange(field.onChange)}
                          options={getOptions(item.name)}
                        />
                      )}
                    />
                  );
                }
                return null;
              })}
            </Stack>
          ))}
          <Stack direction="row" mt={2} gap={2}>
            <MButton
              data-testid="run_report_btn"
              type="submit"
              size="medium"
              disabled={isLoading || !form.formState.isValid}
            >
              Run Report
            </MButton>
          </Stack>
        </Form>
      </ShadowBlock>

      {!!tableData && (
        <Box mt={3}>
          <ShadowBlock customStyles={{ gap: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
              <Typography variant="h5" data-testid="report_title">
                Case Summary Report for {currentStateName} {dateRange}
              </Typography>
              <MButton
                variant="bordered"
                startIcon={<ExportIcon />}
                onClick={exportToPdf}
                disabled={tableData.length <= 1}
              >
                Export PDF
              </MButton>
            </Box>
            {tableData.length > 1 && !isLoading ? (
              <Table columns={productivityByAttorneyColumns} rows={rowsAdapter} hasTotalRow />
            ) : (
              <Box
                display="flex"
                width="1140px"
                flexDirection="column"
                alignItems="center"
                mt={12}
                mb={25}
                mx="auto"
              >
                <EmptyBlock
                  title="No Records"
                  desc="Please change filters to display report"
                  descFontSize={18}
                />
              </Box>
            )}
          </ShadowBlock>
        </Box>
      )}
    </Stack>
  );
};

export default CaseSummary;
